import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import elOrigenImg from '../assets/images/sobre_nosotros/El_Origen.jpg';
import primerosPasosImg from '../assets/images/sobre_nosotros/Primeros_Pasos.png';
import investigacionImg from '../assets/images/sobre_nosotros/Investigación_y_Desarrollo.png';
import prototipadoImg from '../assets/images/sobre_nosotros/Prototipado.png';
import shooting1 from '../assets/images/sobre_nosotros/Shooting_Guerrilla/1.jpg';
import shooting2 from '../assets/images/sobre_nosotros/Shooting_Guerrilla/2.jpg';
import shooting3 from '../assets/images/sobre_nosotros/Shooting_Guerrilla/3.jpg';
import lanzamientoVideo from '../assets/videos/Foirola Vertical.mp4';
import jaggermeisterVideo from '../assets/videos/Video loopers music.mp4';
import mentoriaImg from '../assets/images/sobre_nosotros/Mentoria_y_Crecimiento.jpg';
import navajitasVideo from '../assets/videos/renderNavajitas.mp4';

const TimelineItem = ({ item, index, onInView }) => {
    const { theme } = useTheme();
    return (
        <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40% 0px -40% 0px" }}
            onViewportEnter={() => onInView(item.image)}
            transition={{ duration: 0.15, delay: index * 0.1 }}
            className="relative pl-12 md:pl-0 py-12 md:grid md:grid-cols-5 md:gap-12 items-start group"
        >
            {/* Date - Left side on desktop */}
            <div className={`md:col-span-2 ${index % 2 === 0 ? 'md:text-right' : 'md:order-3 md:text-left'} mb-2 md:mb-0 md:mt-2`}>
                <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-charcoal'} font-sans tracking-tight`}>{item.date}</h3>
            </div>

            {/* Center Line & Dot */}
            <div className={`absolute left-4 md:relative md:left-auto md:col-span-1 flex flex-col items-center h-full transform -translate-x-1/2 md:translate-x-0 ${index % 2 !== 0 ? 'md:order-2' : ''}`}>
                <div className={`w-4 h-4 rounded-full ${theme === 'dark' ? 'bg-white border-dark-bg' : 'bg-charcoal border-off-white'} border-4 z-10 group-hover:scale-150 transition-transform duration-300 mt-3`} />
            </div>

            {/* Content - Right side on desktop */}
            <div className={`md:col-span-2 ${index % 2 === 0 ? 'md:text-left' : 'md:order-1 md:text-right'}`}>
                <h4 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>{item.title}</h4>
                <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} font-light leading-relaxed`}>
                    {item.description}
                </p>
            </div>
        </motion.div>
    );
};

const Timeline = () => {
    const { t } = useLanguage();
    const { theme } = useTheme();
    const [activeImage, setActiveImage] = useState(null);
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    // Parallax effect: Move the background slightly slower than the scroll
    const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
    const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1.3]);

    // Horizontal scroll for gallery
    const x = useTransform(scrollYProgress, [0, 1], ['0%', '-20%']);

    const timelineData = [
        {
            date: t('timeline.jun2024.date'),
            title: t('timeline.jun2024.title'),
            description: t('timeline.jun2024.desc'),
            image: elOrigenImg
        },
        {
            date: t('timeline.jul2024.date'),
            title: t('timeline.jul2024.title'),
            description: t('timeline.jul2024.desc'),
            image: primerosPasosImg
        },
        {
            date: t('timeline.sep2024_1.date'),
            title: t('timeline.sep2024_1.title'),
            description: t('timeline.sep2024_1.desc'),
            image: investigacionImg
        },
        {
            date: t('timeline.sep2024_2.date'),
            title: t('timeline.sep2024_2.title'),
            description: t('timeline.sep2024_2.desc'),
            image: prototipadoImg
        },
        {
            date: t('timeline.oct2024_1.date'),
            title: t('timeline.oct2024_1.title'),
            description: t('timeline.oct2024_1.desc'),
            image: [shooting1, shooting2, shooting3]
        },
        {
            date: t('timeline.oct2024_2.date'),
            title: t('timeline.oct2024_2.title'),
            description: t('timeline.oct2024_2.desc'),
            image: lanzamientoVideo
        },
        {
            date: t('timeline.jan2025.date'),
            title: t('timeline.jan2025.title'),
            description: t('timeline.jan2025.desc'),
            image: jaggermeisterVideo
        },
        {
            date: t('timeline.year2025.date'),
            title: t('timeline.year2025.title'),
            description: t('timeline.year2025.desc'),
            image: mentoriaImg
        },
        {
            date: t('timeline.dec2025.date'),
            title: t('timeline.dec2025.title'),
            description: t('timeline.dec2025.desc'),
            image: navajitasVideo
        }
    ];

    const renderBackground = (content) => {
        if (Array.isArray(content)) {
            return (
                <motion.div
                    className="absolute inset-0 flex flex-row"
                    style={{ x, scale, filter: 'blur(4px)' }}
                >
                    {content.map((img, idx) => (
                        <img key={idx} src={img} alt={`Gallery ${idx}`} className="h-full w-auto object-cover max-w-none opacity-30" />
                    ))}
                </motion.div>
            );
        } else if (typeof content === 'string' && content.endsWith('.mp4')) {
            return (
                <motion.div className="absolute inset-0" style={{ y, scale }}>
                    <video
                        src={content}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover opacity-30"
                        style={{ filter: 'blur(4px)' }}
                    />
                </motion.div>
            );
        } else {
            return (
                <motion.img
                    src={content}
                    alt="Background"
                    style={{ y, scale, filter: 'blur(4px)' }}
                    className="w-full h-full object-cover opacity-30"
                />
            );
        }
    };

    return (
        <div ref={containerRef} className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            {/* Background Image */}
            <div className="fixed inset-0 z-0 pointer-events-none flex items-center justify-center overflow-hidden bg-black">
                <AnimatePresence mode="wait">
                    {activeImage && (
                        <motion.div
                            key={Array.isArray(activeImage) ? 'gallery' : activeImage}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-0"
                        >
                            {renderBackground(activeImage)}
                        </motion.div>
                    )}
                </AnimatePresence>
                {/* Overlay gradient to ensure text readability */}
                <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-dark-bg/80' : 'bg-off-white/80'}`} />
            </div>

            <div className={`absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} -ml-[1px] z-10`} />
            <div className="space-y-0 relative z-10">
                {timelineData.map((item, index) => (
                    <TimelineItem
                        key={index}
                        item={item}
                        index={index}
                        onInView={(img) => {
                            if (img) setActiveImage(img);
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default Timeline;
